import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle, Stat } from '../../components/Card';
import { Switch, TextInput } from '../../components/Field';
import { toast } from '../../components/Toast';
import { useAuthStore } from '../../app/authStore';
import { useSettingsStore } from './settingsStore';
import { errorMessage } from '../../lib/api';
import {
  STALE_AFTER_MAX_MS,
  STALE_AFTER_MIN_MS,
  TELEMETRY_RATE_MAX_HZ,
  TELEMETRY_RATE_MIN_HZ,
} from '@shared/defaults';
import { validateThresholds } from '@shared/validate';
import type { DroneAlertThresholds, PlatformSettings } from '@shared/types';
import { THRESHOLD_GROUPS, thresholdOrderingProblems } from './thresholdGroups';

type ThresholdKey = keyof DroneAlertThresholds;

const LEVELS: { suffix: 'Caution' | 'Warning' | 'Critical'; label: string }[] = [
  { suffix: 'Caution', label: '注意' },
  { suffix: 'Warning', label: '警告' },
  { suffix: 'Critical', label: '严重' },
];

/** 用户清空数字输入框时草稿会短暂变成 NaN，显示成空串而不是 "NaN"。 */
function num(value: number): string {
  return Number.isFinite(value) ? String(value) : '';
}

/**
 * 收集草稿里所有不合法的字段（key 与输入框的 `name` 对应）。
 * 纯函数：随草稿实时求值，改对了红标立刻消失，失败提示里的"标红"才有意义。
 */
function collectErrors(draft: PlatformSettings): Record<string, string> {
  const next: Record<string, string> = {};

  if (draft.platformName.trim() === '') next.platformName = '平台名称不能为空';

  const rates: [keyof PlatformSettings, string, string][] = [
    ['overviewTelemetryRateHz', '总览刷新频率', 'overviewTelemetryRateHz'],
    ['singleDroneTelemetryRateHz', '单机刷新频率', 'singleDroneTelemetryRateHz'],
    ['defaultTelemetryRateHz', '设备默认频率', 'defaultTelemetryRateHz'],
  ];
  for (const [key, label, field] of rates) {
    const value = draft[key] as number;
    if (!Number.isFinite(value) || value < TELEMETRY_RATE_MIN_HZ || value > TELEMETRY_RATE_MAX_HZ) {
      next[field] = `${label}需在 ${TELEMETRY_RATE_MIN_HZ} ~ ${TELEMETRY_RATE_MAX_HZ} Hz 之间`;
    }
  }

  if (
    !Number.isFinite(draft.defaultStaleAfterMs) ||
    draft.defaultStaleAfterMs < STALE_AFTER_MIN_MS ||
    draft.defaultStaleAfterMs > STALE_AFTER_MAX_MS
  ) {
    next.defaultStaleAfterMs = `超时判定需在 ${STALE_AFTER_MIN_MS} ~ ${STALE_AFTER_MAX_MS} ms 之间`;
  }

  if (
    !Number.isInteger(draft.auditRetentionDays) ||
    draft.auditRetentionDays < 1 ||
    draft.auditRetentionDays > 3650
  ) {
    next.auditRetentionDays = '保留天数需为 1 ~ 3650 之间的整数';
  }

  for (const problem of validateThresholds(draft.defaultThresholds)) {
    next[problem.field] = problem.message;
  }

  for (const problem of thresholdOrderingProblems(draft.defaultThresholds)) {
    next[problem.field] = problem.message;
  }

  return next;
}

/** 平台设置：看板频率、默认告警阈值、审计保留策略。 */
export function SettingsPage() {
  const permissions = useAuthStore((state) => state.permissions);
  const canManage = permissions.includes('settings.manage');

  const settings = useSettingsStore((state) => state.settings);
  const loadSettings = useSettingsStore((state) => state.load);
  const saveSettings = useSettingsStore((state) => state.save);
  const loadError = useSettingsStore((state) => state.error);

  const [draft, setDraft] = useState<PlatformSettings>(settings);
  const [saving, setSaving] = useState(false);
  const errors = useMemo(() => collectErrors(draft), [draft]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  // 服务端数据到达（或保存成功）后同步到本地草稿
  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(settings), [draft, settings]);

  function setNumber<K extends keyof PlatformSettings>(key: K, raw: string) {
    const value = raw === '' ? Number.NaN : Number(raw);
    setDraft((current) => ({ ...current, [key]: value }) as PlatformSettings);
  }

  function setThreshold(key: ThresholdKey, raw: string) {
    const value = raw === '' ? Number.NaN : Number(raw);
    setDraft((current) => ({
      ...current,
      defaultThresholds: { ...current.defaultThresholds, [key]: value },
    }));
  }

  async function submit() {
    if (!canManage) return;
    if (Object.keys(errors).length > 0) {
      toast.critical('保存失败', '存在不合法的配置项，请检查标红的字段。');
      return;
    }
    setSaving(true);
    try {
      await saveSettings({
        platformName: draft.platformName.trim(),
        overviewTelemetryRateHz: draft.overviewTelemetryRateHz,
        singleDroneTelemetryRateHz: draft.singleDroneTelemetryRateHz,
        defaultTelemetryRateHz: draft.defaultTelemetryRateHz,
        defaultStaleAfterMs: draft.defaultStaleAfterMs,
        defaultThresholds: draft.defaultThresholds,
        auditRetentionDays: draft.auditRetentionDays,
        allowDroneProbe: draft.allowDroneProbe,
      });
      toast.ok('平台设置已保存');
    } catch (cause) {
      toast.critical('保存失败', errorMessage(cause));
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    setDraft(settings);
  }

  return (
    <div className="flex flex-col gap-3 p-3 lg:p-4">
      {loadError ? (
        <Card tone="critical">
          <CardBody>
            <p className="text-sm text-fg">{loadError}</p>
          </CardBody>
        </Card>
      ) : null}

      {!canManage ? (
        <Card tone="warning">
          <CardBody>
            <p className="text-sm text-fg">当前角色没有「管理平台设置」权限，页面只读。</p>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader
          actions={
            <div className="flex gap-2">
              <Button size="sm" disabled={!dirty || saving} onClick={reset}>
                放弃修改
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={!canManage || !dirty}
                loading={saving}
                onClick={() => void submit()}
              >
                保存设置
              </Button>
            </div>
          }
        >
          <CardTitle subtitle="影响所有无人机的默认行为，单台设备可在注册表中单独覆盖。">
            平台基础配置
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            <TextInput
              label="平台名称"
              disabled={!canManage}
              error={errors.platformName}
              value={draft.platformName}
              onChange={(event) => setDraft({ ...draft, platformName: event.target.value })}
            />
            <div className="flex flex-col gap-1">
              <TextInput
                label="总览刷新频率（Hz）"
                type="number"
                step="0.1"
                min={TELEMETRY_RATE_MIN_HZ}
                max={TELEMETRY_RATE_MAX_HZ}
                disabled={!canManage}
                error={errors.overviewTelemetryRateHz}
                hint="总览页卡片与地图的最高刷新频率；机体 MAVLink 上报率为 1Hz，调高只会拿到重复帧"
                value={num(draft.overviewTelemetryRateHz)}
                onChange={(event) => setNumber('overviewTelemetryRateHz', event.target.value)}
              />
            </div>
            <TextInput
              label="单机刷新频率（Hz）"
              type="number"
              step="0.1"
              min={TELEMETRY_RATE_MIN_HZ}
              max={TELEMETRY_RATE_MAX_HZ}
              disabled={!canManage}
              error={errors.singleDroneTelemetryRateHz}
              hint="单机管理页的最高渲染频率，与浏览器实际收帧解耦；不应高于机体上报率（1Hz）"
              value={num(draft.singleDroneTelemetryRateHz)}
              onChange={(event) => setNumber('singleDroneTelemetryRateHz', event.target.value)}
            />
            <TextInput
              label="设备默认上报频率（Hz）"
              type="number"
              step="0.1"
              min={TELEMETRY_RATE_MIN_HZ}
              max={TELEMETRY_RATE_MAX_HZ}
              disabled={!canManage}
              error={errors.defaultTelemetryRateHz}
              hint="新注册设备的默认 telemetryRateHz，应等于机体 MAVLink 上报率（默认 1Hz）"
              value={num(draft.defaultTelemetryRateHz)}
              onChange={(event) => setNumber('defaultTelemetryRateHz', event.target.value)}
            />
            <TextInput
              label="数据超时判定（ms）"
              type="number"
              step="100"
              min={STALE_AFTER_MIN_MS}
              max={STALE_AFTER_MAX_MS}
              disabled={!canManage}
              error={errors.defaultStaleAfterMs}
              hint="超过该时长没收到帧即标记为失联"
              value={num(draft.defaultStaleAfterMs)}
              onChange={(event) => setNumber('defaultStaleAfterMs', event.target.value)}
            />
            <TextInput
              label="审计日志保留天数"
              type="number"
              step="1"
              min="1"
              max="3650"
              disabled={!canManage}
              error={errors.auditRetentionDays}
              value={num(draft.auditRetentionDays)}
              onChange={(event) => setNumber('auditRetentionDays', event.target.value)}
            />
          </div>

          <div className="mt-3 rounded-lg border border-line bg-surface-2 p-3">
            <Switch
              checked={draft.allowDroneProbe}
              disabled={!canManage}
              label="允许从平台侧主动探测无人机端点"
              hint="开启后维护员可让后端向注册的域名+端口发起探测请求。公网可写角色可能借此访问内网地址（SSRF），生产环境请保持关闭。"
              onChange={(checked) => setDraft({ ...draft, allowDroneProbe: checked })}
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle subtitle="对没有单独配置阈值的无人机生效。数值含义与告警等级一致：注意 / 警告 / 严重。">
            默认告警阈值
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
            {THRESHOLD_GROUPS.map((group) => (
              <div key={group.key} className="rounded-lg border border-line bg-surface-2 p-3">
                <p className="text-sm font-medium text-fg">{group.title}</p>
                <p className="mt-0.5 mb-2 text-xs text-fg-subtle">{group.description}</p>
                {group.metrics.map((metric) => (
                  <div key={metric.prefix} className="grid grid-cols-3 gap-2">
                    {LEVELS.map((level) => {
                      const key = `${metric.prefix}${level.suffix}` as ThresholdKey;
                      return (
                        <TextInput
                          key={key}
                          label={`${level.label}${metric.unit ? `（${metric.unit}）` : ''}`}
                          type="number"
                          step="any"
                          disabled={!canManage}
                          error={level.suffix === 'Caution' ? errors[key] : undefined}
                          value={num(draft.defaultThresholds[key])}
                          onChange={(event) => setThreshold(key, event.target.value)}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle subtitle="这三个数字来自 Worker 运行时配置，只读展示，便于排查告警行为。">
            当前生效摘要
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="总览频率" value={`${settings.overviewTelemetryRateHz} Hz`} />
            <Stat label="单机频率" value={`${settings.singleDroneTelemetryRateHz} Hz`} />
            <Stat label="超时判定" value={`${settings.defaultStaleAfterMs} ms`} />
            <Stat label="审计保留" value={`${settings.auditRetentionDays} 天`} />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle subtitle="下限型指标低于阈值即告警；上限型指标高于阈值即告警。">
            阈值语义
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="flex flex-col gap-2 text-xs text-fg-muted">
            <p>
              「下限型」指标：电量百分比、单体电压、可见卫星数、光流质量、链路质量 —— 低于阈值即告警。
            </p>
            <p>
              「上限型」指标：HDOP、链路延迟、姿态角、电池温度 —— 高于阈值即告警。
            </p>
            <p>
              每组数值必须单调：下限型 注意 ≥ 警告 ≥ 严重，上限型 注意 ≤ 警告 ≤ 严重
              —— 即数值越高（下限型）或越低（上限型）越宽松，否则同一时刻会同时命中多个等级。
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
